import {
  AppBar,
  Box,
  Button,
  Drawer,
  IconButton,
  InputAdornment,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { useResponsive } from "../hooks/uiHook";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const AppBarSearch = ({
  handleSubmit,
  handleSearch,
  searchText,
  title,
  buttonTitle,
  buttonColor,
  buttonIcon,
  onExport,
  onToggleViewMode,
  viewMode,
  children,
  actions,
  disableMobileDrawer = false,
}) => {
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsive();
  const [filterOpen, setFilterOpen] = useState(false);
  // `disableMobileDrawer` only keeps `children` out of the Drawer — it's for
  // screens whose children is just a single button that opens its own dialog
  // (e.g. FiltersButton/FiltersDialog); nesting that inside this Drawer left
  // two MUI modals open at once (this Drawer + the dialog it triggers),
  // which triggers a "Blocked aria-hidden on an element because its
  // descendant retained focus" console warning. It does NOT affect
  // `actionButtons` (Add/Export/view-toggle) — those never open a nested
  // modal, so they still collapse into the Drawer on small screens same as
  // every other screen, instead of always sitting inline and cluttering a
  // narrow header.
  const showChildrenInline = !isSmallScreen || disableMobileDrawer;
  const showActionsInline = !isSmallScreen;
  // Screens like Catégories/Produits/Variations/Ingrédiants only have a
  // single "Ajouter X" action on mobile (view toggle/export/filters are
  // either absent or already hidden on small screens). Forcing those behind
  // a filter-icon-opens-a-drawer-for-one-button flow is an extra tap for no
  // benefit, so they collapse straight to a "+" icon that calls handleSubmit
  // directly instead of rendering the Drawer at all.
  const isSingleAddButtonOnly =
    Boolean(handleSubmit) &&
    !onExport &&
    !onToggleViewMode &&
    !actions &&
    (disableMobileDrawer || !children);
  const drawerHasActions =
    !showActionsInline &&
    Boolean(
      actions ||
        onExport ||
        onToggleViewMode ||
        (handleSubmit && !isSingleAddButtonOnly)
    );
  // Only offer the mobile collapse affordance when there's actually
  // something to put in it — otherwise a screen with no filters/actions
  // (e.g. Devise) still showed a filter icon that opened an empty drawer.
  const hasCollapsibleContent =
    (!disableMobileDrawer && Boolean(children)) || drawerHasActions;
  const [searchValue, setSearchValue] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchTimer = useRef(null);
  const searchInputRef = useRef(null);
  // Phone: the field stays collapsed until tapped, then it replaces the
  // title on the same row. A typed query keeps it open.
  const searchExpanded = !isSmallScreen || searchOpen || Boolean(searchValue);

  useEffect(() => () => clearTimeout(searchTimer.current), []);

  const onSearchChange = (event) => {
    const value = event.target.value;
    setSearchValue(value);
    if (!handleSearch) return;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      handleSearch({ target: { value } });
    }, 500);
  };

  const onClearSearch = () => {
    setSearchValue("");
    clearTimeout(searchTimer.current);
    if (handleSearch) handleSearch({ target: { value: "" } });
  };

  const actionButtons = (
    <>
      {actions}
      {onExport && (
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          startIcon={<CloudDownloadIcon />}
          onClick={onExport}
        >
          {t("filters.exportCsvShort")}
        </Button>
      )}
      {handleSubmit && !(isSmallScreen && isSingleAddButtonOnly) && (
          <Button
            variant="contained"
            color={buttonColor ? buttonColor : "success"}
            size="small"
            startIcon={buttonIcon ? buttonIcon : <AddIcon />}
            onClick={handleSubmit}
            sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
          >
            {buttonTitle}
          </Button>
        )}
      {onToggleViewMode && (
        <Tooltip
          title={
            viewMode === "list" ? t("filters.viewGrid") : t("filters.viewList")
          }
        >
          <IconButton color="inherit" onClick={onToggleViewMode}>
            {viewMode === "list" ? <ViewModuleIcon /> : <ViewListIcon />}
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  return (
    <AppBar position="relative" sx={{ width: "100%" }}>
      <Toolbar
        sx={{
          width: "100%",
          minHeight: 64,
          display: "flex",
          flexWrap: "nowrap",
          gap: 1,
          py: 1.25,
          px: { xs: 1, sm: 2 },
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "nowrap",
            minWidth: 0,
            flex: "1 1 auto",
            overflow: "hidden",
          }}
        >
          {!(isSmallScreen && searchExpanded) && (
            <Typography
              variant="h5"
              color="inherit"
              noWrap
              sx={{ flex: "0 1 auto", minWidth: 0 }}
            >
              {title}
            </Typography>
          )}
          {isSmallScreen && hasCollapsibleContent && !isSingleAddButtonOnly && (
            <IconButton onClick={() => setFilterOpen(true)} sx={{ flexShrink: 0 }}>
              <FilterListIcon />
            </IconButton>
          )}
          {handleSearch && !searchExpanded && (
            <IconButton
              color="inherit"
              aria-label={searchText ?? t("search.default")}
              onClick={() => {
                setSearchOpen(true);
                setTimeout(() => searchInputRef.current?.focus(), 0);
              }}
              sx={{ flexShrink: 0 }}
            >
              <SearchIcon />
            </IconButton>
          )}
          {handleSearch && searchExpanded && (
            <Box
              sx={{
                flex: { xs: "1 1 0", md: "1 1 280px" },
                maxWidth: { md: 420 },
                minWidth: 0,
              }}
            >
              <TextField
                type="text"
                variant="outlined"
                size="small"
                fullWidth
                placeholder={searchText ?? t("search.default")}
                value={searchValue}
                onChange={onSearchChange}
                inputRef={searchInputRef}
                sx={{
                  backgroundColor: "background.paper",
                  borderRadius: 1,
                  "& .MuiOutlinedInput-input": {
                    paddingTop: "6px",
                    paddingBottom: "6px",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment:
                    searchValue || isSmallScreen ? (
                      <InputAdornment position="end">
                        <IconButton
                          type="button"
                          size="small"
                          aria-label={t("common.clear")}
                          onClick={() => {
                            if (searchValue) onClearSearch();
                            if (isSmallScreen) setSearchOpen(false);
                          }}
                          sx={{ p: 0.5 }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                }}
              />
            </Box>
          )}
          {showChildrenInline && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
              }}
            >
              {children}
            </Box>
          )}
          {isSmallScreen && isSingleAddButtonOnly && (
            <Tooltip title={buttonTitle}>
              <IconButton
                onClick={handleSubmit}
                aria-label={buttonTitle}
                sx={{
                  flexShrink: 0,
                  ml: "auto",
                  bgcolor: `${buttonColor ? buttonColor : "success"}.main`,
                  color: `${buttonColor ? buttonColor : "success"}.contrastText`,
                  "&:hover": {
                    bgcolor: `${buttonColor ? buttonColor : "success"}.dark`,
                  },
                }}
              >
                {buttonIcon ? buttonIcon : <AddIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {showActionsInline && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1,
              flexWrap: "nowrap",
              flex: "0 0 auto",
              ml: "auto",
            }}
          >
            {actionButtons}
          </Box>
        )}
        {hasCollapsibleContent && !isSingleAddButtonOnly && (
          <Drawer
            anchor="right"
            open={filterOpen}
            onClose={() => setFilterOpen(false)}
          >
            <Box
              sx={{
                p: 2,
                width: 320,
                maxWidth: "85vw",
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
                "& > *": {
                  width: "100% !important",
                  maxWidth: "none !important",
                },
              }}
            >
              {!disableMobileDrawer && children}
              {drawerHasActions && actionButtons}
            </Box>
          </Drawer>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default AppBarSearch;
